// Customer › Finance — what this customer costs us, and exactly which resources
// make up that figure.
//
// One rolling-30-day number at the top, then that number broken down by AWS
// service — direct spend and allocated shared cost kept separable throughout.
// Every section has to reconcile to the headline: a per-customer cost page whose
// parts don't add up is worse than no page, because it gets quoted at customers.
//
// Totals come from /finance/customer/{id} (the stored daily series, same numbers
// the fleet Review table publishes). The resource lines come from
// /finance/customer/{id}/breakdown, costed over the same window.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Collapse, MenuItem, TextField } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  getFinanceCustomer, getFinanceCustomerBreakdown, saveFinanceSettings,
  type FinanceCustomerCosts, type FinanceCustomerBreakdown,
} from "../../lib/brain-api";
import { brainTokens } from "../../design/brainTheme";
import { SectionCard, Icon, AreaChart } from "../../mui";
import { Explain } from "../../agent/Explain";
import { fmtUSD, fmtBytes } from "../../mui/format";
import { serviceLabel, servicePurpose, regionLabel, fmtShare, fmtUSDPrecise, fmtDayShort } from "./finance-ui";

const c = brainTokens.color;
const mono = brainTokens.font.mono;

const TIER_LABELS: Record<string, string> = { small: "Small", medium: "Medium", large: "Large" };
const DATA_TIERS = ["small", "medium", "large"];

/** Below this, a percentage change is arithmetic noise on a rounding difference. */
const DELTA_FLOOR_USD = 2;

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-US", { month: "short", year: "numeric" });
}

export function CustomerFinanceTabMui({ customerId }: { customerId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<FinanceCustomerCosts | null>(null);
  const [bd, setBd] = useState<FinanceCustomerBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [tier, setTier] = useState("");
  const [sub, setSub] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The breakdown is additive detail. If it fails, the totals still render —
      // losing the resource list must not blank out the whole tab.
      const [d, b] = await Promise.all([
        getFinanceCustomer(customerId),
        getFinanceCustomerBreakdown(customerId).catch(() => null),
      ]);
      setData(d);
      setBd(b);
      setTier(d.finance.dataTier ?? "");
      setSub(d.finance.subscriptionMonthly != null ? String(d.finance.subscriptionMonthly) : "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Costs unavailable");
    } finally {
      setLoading(false);
    }
  }, [customerId]);
  useEffect(() => {
    void load();
  }, [load]);

  const { monthly, footprint, latestUsage } = useMemo(() => {
    // Loaded cost per month: direct + this code's share of the shared pool.
    const byMonth = new Map<string, number>();
    for (const r of data?.rows ?? []) {
      byMonth.set(r.month, (byMonth.get(r.month) ?? 0) + r.cost + (r.allocated ?? 0));
    }
    const months = [...byMonth.keys()].sort().map((m) => ({ month: m, cost: byMonth.get(m) ?? 0 }));

    // Data footprint: the customer's LARGEST backup per month and nothing else.
    // Mixing in EBS/RDS bytes made the series jump between two different
    // measurements of two different things.
    const bytesByMonth = new Map<string, number>();
    for (const u of data?.usage ?? []) {
      if (!u.backupBytes) continue;
      bytesByMonth.set(u.month, Math.max(bytesByMonth.get(u.month) ?? 0, u.backupBytes));
    }
    const measured = [...bytesByMonth.keys()].sort().map((m) => ({ month: m, bytes: bytesByMonth.get(m) ?? 0 }));

    return {
      monthly: months,
      footprint: measured,
      latestUsage: (data?.usage ?? []).slice().sort((a, b) => b.month.localeCompare(a.month))[0],
    };
  }, [data]);

  if (loading) return <Box sx={{ color: c.muted2, fontSize: 14, py: 4 }}>Loading finance…</Box>;

  if (error) {
    const restricted = /403|finance|admin|forbidden/i.test(error);
    return (
      <Notice
        icon="shield"
        title={restricted ? "Costs restricted" : "Costs unavailable"}
        body={restricted ? "Visible to admins and the finance department only." : error}
      />
    );
  }
  if (!data || data.codes.length === 0) {
    return (
      <Notice
        icon="dollar"
        title="No account code"
        body="No AWS account code is mapped to this customer, so nothing on the bill can be attributed to them yet."
      />
    );
  }

  const direct = bd?.direct ?? 0;
  const allocated = bd?.allocated ?? 0;
  const loaded = direct + allocated;
  const prevLoaded = (bd?.prevDirect ?? 0) + (bd?.prevAllocated ?? 0);

  const subNum = sub ? Number(sub) : data.finance.subscriptionMonthly;
  const margin = subNum != null ? subNum - loaded : null;

  const saveSettings = async () => {
    setSaving(true);
    try {
      await saveFinanceSettings(customerId, {
        dataTier: tier || undefined,
        subscriptionMonthly: sub ? Number(sub) : null,
      });
      enqueueSnackbar("Pricing saved.", { variant: "success" });
      setEditing(false);
      await load();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Save failed.", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <BillHeader
        direct={direct}
        allocated={allocated}
        prevLoaded={prevLoaded}
        window={bd}
        codes={data.codes}
      />

      <ServiceSplit breakdown={bd} />

      <SectionCard
        label="Pricing & margin"
        labelIcon={<Icon name="dollar" size={14} stroke={1.8} />}
        sx={{ mb: "16px" }}
        headerMb={18}
        actions={
          <>
            <Button variant="outlined" size="small" component={RouterLink} to={`/customers/${customerId}/pricing-sheet`} startIcon={<Icon name="book" size={14} stroke={1.8} />}>
              Pricing sheet
            </Button>
            {editing ? (
              <>
                <Button size="small" onClick={() => setEditing(false)} sx={{ color: c.ink3 }}>Cancel</Button>
                <Button size="small" variant="contained" disabled={saving} onClick={() => void saveSettings()}>Save</Button>
              </>
            ) : (
              <Button variant="outlined" size="small" onClick={() => setEditing(true)} startIcon={<Icon name="edit" size={14} stroke={1.8} />}>
                Set pricing
              </Button>
            )}
          </>
        }
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: "14px" }}>
          <SubCard label="Subscription" hint="per month">
            {editing ? (
              <TextField size="small" fullWidth type="number" value={sub} onChange={(e) => setSub(e.target.value)} InputProps={{ startAdornment: <Box sx={{ mr: 0.5, color: c.faint }}>$</Box> }} />
            ) : (
              <BigValue value={subNum != null ? fmtUSD(subNum) : "—"} muted={subNum == null} />
            )}
          </SubCard>
          <SubCard label="Margin" hint={margin == null ? "needs a subscription amount" : "subscription less loaded cost"}>
            <BigValue
              value={margin != null ? fmtUSD(margin) : "—"}
              muted={margin == null}
              color={margin != null ? (margin >= 0 ? c.successText : c.dangerText) : undefined}
            />
            {margin != null && subNum ? (
              <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint, mt: "6px" }}>
                {Math.round((margin / subNum) * 100)}% of the subscription
              </Box>
            ) : null}
          </SubCard>
          <SubCard label="Data tier" hint="manual sizing bucket">
            {editing ? (
              <TextField select size="small" fullWidth value={tier} onChange={(e) => setTier(e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {DATA_TIERS.map((t) => (
                  <MenuItem key={t} value={t}>{TIER_LABELS[t]}</MenuItem>
                ))}
              </TextField>
            ) : (
              <BigValue value={data.finance.dataTier ? TIER_LABELS[data.finance.dataTier] ?? data.finance.dataTier : "—"} muted={!data.finance.dataTier} />
            )}
          </SubCard>
          <SubCard label="Largest backup" hint="drives the data tier">
            <BigValue value={fmtBytes(latestUsage?.backupBytes)} muted={!latestUsage?.backupBytes} />
            {latestUsage?.instanceTypes?.length ? (
              <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint, mt: "6px" }}>
                on {latestUsage.instanceTypes.join(", ")}
              </Box>
            ) : null}
          </SubCard>
        </Box>
      </SectionCard>

      <FootprintCard points={footprint} />

      <SectionCard label="Loaded cost · by month" labelIcon={<Icon name="chart" size={14} stroke={1.8} />} sx={{ mb: "16px" }}>
        <AreaChart
          values={monthly.map((x) => x.cost)}
          height={190}
          leftLabel={monthly[0] ? monthLabel(monthly[0].month) : undefined}
          rightLabel={
            monthly.length
              ? `${monthLabel(monthly[monthly.length - 1].month)} (so far) · ${fmtUSD(monthly[monthly.length - 1].cost)}`
              : undefined
          }
        />
        <Box sx={{ fontSize: 11.5, color: c.faint, mt: "10px", lineHeight: 1.6 }}>
          Direct spend plus allocated shared cost, per calendar month. The last point is the
          month in progress, so it always reads low.
        </Box>
      </SectionCard>
    </Box>
  );
}

// --- the headline ------------------------------------------------------------

function BillHeader({
  direct, allocated, prevLoaded, window: win, codes,
}: {
  direct: number;
  allocated: number;
  prevLoaded: number;
  window: FinanceCustomerBreakdown | null;
  codes: string[];
}) {
  const loaded = direct + allocated;
  const delta = loaded - prevLoaded;
  const pct = prevLoaded > 0 ? (delta / prevLoaded) * 100 : null;
  const meaningful = Math.abs(delta) >= DELTA_FLOOR_USD && pct != null;
  const directPct = loaded > 0 ? (direct / loaded) * 100 : 0;

  return (
    <Box
      sx={{
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: `${brainTokens.radius.card}px`,
        p: "22px 24px 20px",
        mb: "16px",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 1fr) minmax(280px, 420px)" },
        gap: "28px",
        alignItems: "start",
      }}
    >
      <Box>
        <Box sx={{ fontFamily: mono, fontSize: 10.5, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: c.labelMuted }}>
          Loaded cost · last {win?.days ?? 30} days
        </Box>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: "14px", mt: "8px" }}>
          <Box sx={{ fontSize: 46, fontWeight: 700, letterSpacing: "-1.6px", lineHeight: 1, color: c.ink }}>
            <Explain
              snapshot={{ label: "Loaded AWS cost, last 30 days", value: loaded, unit: "USD" }}
              prompt="Explain what makes up this customer's loaded AWS cost — the biggest resources, and what share of it is allocated shared infrastructure. Cite the resource lines."
            >
              {fmtUSD(loaded)}
            </Explain>
          </Box>
          {meaningful ? (
            <Box sx={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: delta >= 0 ? c.dangerText3 : c.successText2 }}>
              {delta >= 0 ? "▲" : "▼"} {Math.abs(Math.round(pct!))}%
            </Box>
          ) : (
            <Box sx={{ fontFamily: mono, fontSize: 12, color: c.faint }}>flat</Box>
          )}
        </Box>
        <Box sx={{ fontSize: 12.5, color: c.muted, mt: "8px", lineHeight: 1.6 }}>
          {win?.from ? `${fmtDayShort(win.from)} → ${fmtDayShort(win.to)}` : "window unavailable"}
          {" · "}
          {prevLoaded > 0 ? `${fmtUSD(prevLoaded)} the 30 days before` : "no prior window to compare"}
        </Box>
        <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint, mt: "10px" }}>
          {codes.map((x) => x.toUpperCase()).join(" · ")}
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: "flex", height: 10, borderRadius: "999px", overflow: "hidden", background: c.borderLight2 }}>
          <Box sx={{ width: `${directPct}%`, background: c.teal }} />
          <Box sx={{ width: `${100 - directPct}%`, background: c.billShared }} />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", mt: "14px" }}>
          <SplitStat swatch={c.teal} label="Theirs alone" value={direct} total={loaded} note="resources only they use" />
          <SplitStat swatch={c.billShared} label="Shared" value={allocated} total={loaded} note="their slice of shared infrastructure" />
        </Box>
      </Box>
    </Box>
  );
}

function SplitStat({ swatch, label, value, total, note }: { swatch: string; label: string; value: number; total: number; note: string }) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
        <Box sx={{ width: 9, height: 9, borderRadius: "2px", background: swatch, flex: "0 0 auto" }} />
        <Box sx={{ fontSize: 11.5, fontWeight: 600, color: c.ink3 }}>{label}</Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "7px", mt: "5px" }}>
        <Box sx={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.4px", color: c.ink }}>{fmtUSD(value)}</Box>
        <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint }}>
          {total > 0 ? fmtShare(value / total) : "—"}
        </Box>
      </Box>
      <Box sx={{ fontSize: 11, color: c.faint, mt: "3px", lineHeight: 1.5 }}>{note}</Box>
    </Box>
  );
}

/** The sharing rule, spelled out. Shared cost is split by region on each
 *  customer's own direct spend there, so this is the arithmetic behind it. */
function ShareWorking({ regions }: { regions: FinanceCustomerBreakdown["regions"] }) {
  const [open, setOpen] = useState(false);
  // Only regions that actually charged them. A region whose pool they get no
  // slice of is the answer to a different question, so it goes in one sentence
  // below rather than as a row of zeroes.
  const charged = regions.filter((r) => r.amount > 0.005);
  const skipped = regions.filter((r) => r.pool > 0.005 && r.amount <= 0.005);

  return (
    <Box sx={{ mt: "14px" }}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer",
          fontSize: 12, color: c.teal, fontWeight: 500,
          font: "inherit", background: "none", border: 0, p: 0,
        }}
      >
        <Icon name="info" size={13} stroke={1.8} />
        How their share is worked out
      </Box>
      <Collapse in={open}>
        <Box sx={{ mt: "12px", p: "14px 16px", bgcolor: c.tealTint2, border: `1px solid ${c.tealTintBorder}`, borderRadius: "8px" }}>
          <Box sx={{ fontSize: 12, color: c.ink3, lineHeight: 1.7, mb: "12px" }}>
            Shared infrastructure is split per region, on each customer's own direct spend
            in that region — a NAT gateway in Canada belongs to the customers who run there,
            not to everyone. MOD (our internal tooling) is never allocated out.
          </Box>
          {charged.map((r) => (
            <Box
              key={r.region}
              sx={{ display: "flex", justifyContent: "space-between", gap: "14px", py: "7px", borderTop: `1px solid ${c.tealTintBorder}`, fontFamily: mono, fontSize: 11.5 }}
            >
              <Box sx={{ color: c.ink3 }}>{regionLabel(r.region)}</Box>
              <Box sx={{ color: c.muted, textAlign: "right" }}>
                {fmtUSDPrecise(r.direct)} of their spend here → {fmtShare(r.share)} of the{" "}
                {fmtUSDPrecise(r.pool)} pool = <Box component="span" sx={{ color: c.ink2 }}>{fmtUSDPrecise(r.amount)}</Box>
              </Box>
            </Box>
          ))}
          {skipped.length > 0 && (
            <Box sx={{ fontSize: 11.5, color: c.muted, mt: "12px", pt: "10px", borderTop: `1px solid ${c.tealTintBorder}`, lineHeight: 1.6 }}>
              {skipped.length} other {skipped.length === 1 ? "region carries" : "regions carry"} shared
              cost ({fmtUSDPrecise(skipped.reduce((a, r) => a + r.pool, 0))}), but they run nothing
              there, so none of it lands on them.
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// --- data footprint ----------------------------------------------------------

/**
 * Footprint growth from the largest backup per month.
 *
 * The raw series saws up and down because some data is backed up daily and some
 * weekly, so a month whose full backup didn't land reads as a shrink. The line
 * carries the peak forward — the high-water mark — and the dots keep every actual
 * measurement visible, so a genuine shrink still shows as dots below the line
 * rather than being hidden by the smoothing.
 */
function FootprintCard({ points }: { points: { month: string; bytes: number }[] }) {
  if (points.length === 0) return null;

  const peaks: number[] = [];
  let running = 0;
  for (const p of points) {
    running = Math.max(running, p.bytes);
    peaks.push(running);
  }

  const latest = points[points.length - 1];
  const peak = peaks[peaks.length - 1];
  const yearAgo = points.length > 12 ? peaks[peaks.length - 13] : peaks[0];
  const growth = yearAgo > 0 ? ((peak - yearAgo) / yearAgo) * 100 : null;
  const shrunk = latest.bytes < peak * 0.95;

  return (
    <SectionCard
      label="Data footprint"
      labelIcon={<Icon name="hex" size={14} stroke={1.8} />}
      sx={{ mb: "16px" }}
      headerMb={8}
      actions={
        <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint }}>
          {fmtBytes(peak)} peak
          {growth != null ? ` · ${growth >= 0 ? "+" : ""}${Math.round(growth)}% over ${Math.min(points.length - 1, 12)} months` : ""}
        </Box>
      }
    >
      <Box sx={{ fontSize: 12.5, color: c.muted, mb: "16px", lineHeight: 1.6 }}>
        Their largest single backup each month, carried forward. Backup cadence differs by
        dataset, so the raw month-to-month figure dips whenever a full backup lands outside
        the window — the line follows the high-water mark instead.
      </Box>
      <FootprintChart points={points} peaks={peaks} />
      <Box sx={{ display: "flex", gap: "22px", mt: "14px", flexWrap: "wrap", fontSize: 11.5, color: c.muted }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <Box sx={{ width: 14, height: 2, background: c.tealInk2, borderRadius: "2px" }} />
          Peak footprint, carried forward
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "50%", border: `1.5px solid ${c.billShared}`, background: c.white }} />
          Largest backup measured that month
        </Box>
        <Box sx={{ fontFamily: mono, color: c.faint }}>
          {monthLabel(latest.month)} · {fmtBytes(latest.bytes)} measured
          {shrunk ? " (below peak — data was removed, or a full backup is missing)" : ""}
        </Box>
      </Box>
    </SectionCard>
  );
}

function FootprintChart({ points, peaks }: { points: { month: string; bytes: number }[]; peaks: number[] }) {
  const W = 1000;
  const H = 200;
  const padY = 16;
  const max = Math.max(...peaks, 1);
  const stepX = points.length > 1 ? W / (points.length - 1) : W;
  const y = (v: number) => H - padY - (v / max) * (H - padY * 2);
  const x = (i: number) => i * stepX;

  // Stepped, because the value is "the most we have ever seen" — it holds flat
  // and jumps when a bigger backup appears. A smooth line would imply we
  // measured growth in between.
  const steps: string[] = [`M0,${y(peaks[0]).toFixed(1)}`];
  if (peaks.length === 1) steps.push(`L${W},${y(peaks[0]).toFixed(1)}`);
  for (let i = 1; i < peaks.length; i++) {
    steps.push(`L${x(i).toFixed(1)},${y(peaks[i - 1]).toFixed(1)}`);
    steps.push(`L${x(i).toFixed(1)},${y(peaks[i]).toFixed(1)}`);
  }
  const line = steps.join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  const gid = "footprint-fill";

  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" sx={{ width: "100%", height: 200, display: "block", overflow: "visible" }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c.tealInk2} stopOpacity="0.18" />
          <stop offset="1" stopColor={c.tealInk2} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={c.tealInk2} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      {points.map((p, i) => (
        <circle
          key={p.month}
          cx={x(i)}
          cy={y(p.bytes)}
          r="3.5"
          fill={c.white}
          stroke={c.billShared}
          strokeWidth="1.6"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </Box>
  );
}

// --- cost by service --------------------------------------------------------
// The page's primary breakdown, directly under the headline: the same figure,
// split by what it buys. Direct spend and allocated shared cost stay separable on
// every row, because "$94 of S3" and "$31 of someone else's S3" are different
// facts about the same bill.

function ServiceSplit({ breakdown: bd }: { breakdown: FinanceCustomerBreakdown | null }) {
  const rows = useMemo(() => {
    const entries = Object.entries(bd?.byService ?? {}).map(([service, v]) => ({
      service,
      direct: v.direct,
      shared: v.shared,
      total: v.direct + v.shared,
    }));
    return entries.filter((r) => r.total > 0.005).sort((a, b) => b.total - a.total);
  }, [bd]);

  if (!bd || !rows.length) {
    return (
      <SectionCard label="Where the money goes" labelIcon={<Icon name="grid" size={14} stroke={1.8} />} sx={{ mb: "16px" }}>
        <Box sx={{ fontSize: 13, color: c.muted2, lineHeight: 1.7 }}>
          No service breakdown for this window. It's written by the nightly cost pipeline —
          run a refresh from the Finance page and it appears on the next pass.
        </Box>
      </SectionCard>
    );
  }

  const loaded = bd.direct + bd.allocated;
  const max = rows[0].total;

  return (
    <SectionCard
      label="Where the money goes"
      labelIcon={<Icon name="grid" size={14} stroke={1.8} />}
      sx={{ mb: "16px" }}
      headerMb={8}
      actions={
        <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint }}>
          {rows.length} services · same {bd.days} days
        </Box>
      }
    >
      <Box sx={{ fontSize: 12.5, color: c.muted, mb: "22px", lineHeight: 1.6 }}>
        The {fmtUSD(loaded)} above, by what it buys. Solid teal is their own resources; the
        lighter segment is their share of shared infrastructure.
      </Box>

      {rows.map((r) => (
        <Box key={r.service} sx={{ mb: "16px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "14px", mb: "7px" }}>
            <Box sx={{ minWidth: 0, fontSize: 13, fontWeight: 500, color: c.ink2 }}>
              {serviceLabel(r.service)}
              {servicePurpose(r.service) ? (
                <Box component="span" sx={{ fontWeight: 400, color: c.faint, ml: "8px", fontSize: 11.5 }}>
                  {servicePurpose(r.service)}
                </Box>
              ) : null}
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "12px", flex: "0 0 auto" }}>
              {/* Both halves, so a row that is mostly allocated cost says so. */}
              <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint }}>
                {r.direct > 0.005 ? fmtUSDPrecise(r.direct) : "—"}
                {" + "}
                {r.shared > 0.005 ? fmtUSDPrecise(r.shared) : "—"}
              </Box>
              <Box sx={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: c.ink, minWidth: 62, textAlign: "right" }}>
                {fmtUSDPrecise(r.total)}
              </Box>
              <Box sx={{ fontFamily: mono, fontSize: 11, color: c.faint, minWidth: 38, textAlign: "right" }}>
                {fmtShare(r.total / loaded)}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", height: 8, borderRadius: "999px", overflow: "hidden", background: c.borderLight2 }}>
            <Box sx={{ width: `${(r.direct / max) * 100}%`, background: c.teal }} />
            <Box sx={{ width: `${(r.shared / max) * 100}%`, background: c.billShared }} />
          </Box>
        </Box>
      ))}

      <Box sx={{ mt: "20px", pt: "14px", borderTop: `1px solid ${c.border}`, fontFamily: mono, fontSize: 12, color: c.ink3 }}>
        {fmtUSD(bd.direct)} theirs + {fmtUSD(bd.allocated)} shared ={" "}
        <Box component="span" sx={{ fontWeight: 500, color: c.ink }}>{fmtUSD(loaded)}</Box> loaded
      </Box>

      {bd.regions.some((r) => r.amount > 0) && <ShareWorking regions={bd.regions} />}
    </SectionCard>
  );
}

// --- small shared bits -------------------------------------------------------

function Notice({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <Box sx={{ background: c.white, border: `1px solid ${c.border}`, borderRadius: "8px", p: "52px 22px", textAlign: "center" }}>
      <Box sx={{ color: c.placeholder, mb: "14px", display: "flex", justifyContent: "center" }}>
        <Icon name={icon} size={30} stroke={1.6} />
      </Box>
      <Box sx={{ fontSize: 16, fontWeight: 600, mb: "8px" }}>{title}</Box>
      <Box sx={{ fontSize: 13, color: c.muted2, maxWidth: 560, mx: "auto", lineHeight: 1.7 }}>{body}</Box>
    </Box>
  );
}

function SubCard({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: "14px 16px", borderRadius: "10px", border: `1px solid ${c.borderLight}`, bgcolor: c.panelBg3 }}>
      <Box sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: c.labelMuted, mb: "8px" }}>
        {label}
        {hint && <Box component="span" sx={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: c.faint, ml: "6px" }}>{hint}</Box>}
      </Box>
      {children}
    </Box>
  );
}

function BigValue({ value, muted, color }: { value: string; muted?: boolean; color?: string }) {
  return (
    <Box sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.3px", color: color ?? (muted ? c.faint : c.ink), lineHeight: 1.2 }}>
      {value}
    </Box>
  );
}
